import { OnboardingState, StepId } from "./types";

/**
 * A slug per step, so a refresh lands back where the person was rather than at
 * the beginning, and so the browser's own Back and Forward do what a phone
 * user's back gesture already expects them to do.
 *
 * Slugs are stable, human-readable and carry no data — never an email, a name
 * or an id. The URL is shared, logged and sits in browser history.
 */
export const STEP_SLUGS: Record<StepId, string> = {
  role: "",
  email: "email",
  email_code: "verify",
  account: "your-details",
  account_exists: "existing-account",
  s_address: "address",
  s_confirm_house: "your-center",
  s_house_details: "center-details",
  s_launched: "done",
  s_next: "whats-next",
  s_build_list: "add-people",
  s_invite_team: "your-team",
  s_list_or_route: "first-outreach",
  a_intent: "how-you-help",
  a_address: "your-address",
  a_house: "centers-near-you",
  a_availability: "your-availability",
  a_import: "your-contacts",
  a_donate: "support",
  a_saved: "welcome",
};

const BY_SLUG: Record<string, StepId> = Object.entries(STEP_SLUGS).reduce(
  (acc, [step, slug]) => ({ ...acc, [slug]: step as StepId }),
  {}
);

export const slugToStep = (slug: string): StepId | null => BY_SLUG[slug] ?? null;

/**
 * The full URL for a step. `role`'s slug is "" — naively appending it to the
 * prefix would produce a trailing-slash URL ("…/onboarding/") that is a
 * different path from the registered bare route ("…/onboarding"), so the
 * empty-slug case is built without the extra segment instead of leaving that
 * to whatever react-router happens to do with it.
 */
/**
 * Where the signup flow lives.
 *
 * Was `/82417/preview/onboarding` — an unguessable prefix so only people handed
 * the link could reach the redesign while it was being built. That is over: this
 * IS registration now, reached from the Register button like anything else
 * (owner, 2026-09-24: "we're done with the test, and this needs to be the
 * accessible, new registration flow for everyone").
 *
 * Defined here and re-exported by backend/sagas/onboardingRedirect so the route
 * table, the post-sign-in redirect and every step URL agree by construction
 * rather than by three hand-copied strings.
 */
export const ONBOARDING_ROOT = "/registration";

export const stepPath = (step: StepId): string => {
  const slug = STEP_SLUGS[step];
  return slug ? `${ONBOARDING_ROOT}/${slug}` : ONBOARDING_ROOT;
};

/**
 * What must already be true before a step may be shown.
 *
 * URLs are guessable, so this is a security boundary and not a convenience:
 * without it, anyone could type /account and register having chosen neither a
 * role nor a center, walking straight around every screen that validates.
 * Each entry returns the step to send them back to, or null if satisfied.
 *
 * Two regimes, split at account creation. BEFORE it, the checks are about what
 * the person has entered. AFTER it, there is exactly one check — is this
 * browser signed in — because by then the server, not the browser, decides.
 */
const requiresSession = (s: OnboardingState): StepId | null => (s.signedIn ? null : "account");

/**
 * The mirror image of requiresSession: a step that must NOT be shown once the account
 * exists.
 *
 * Every pre-account screen used to stay reachable for ever. `email`'s only requirement
 * was "has a path been chosen", which is still true afterwards — so a signed-in person
 * who pressed Back, or typed /registration/verify, landed on the email or verification
 * screen for an account that had already been created and verified. Worse, the app's own
 * Back from the first post-account screen went to `account`, the create-your-account
 * form, where submitting would fail with "A user with this email already exists."
 *
 * Owner, 2026-09-24: "verify that navigating back and forth doesn't cause issues during
 * registration — even after account creation (though, naturally, there is no 'back'
 * option to the email verification screen, etc.)."
 *
 * Bounces to where the person actually belongs: the first step of their path that still
 * needs doing. firstUnsatisfiedStep resolves the rest from there.
 */
const refusedOnceSignedIn = (s: OnboardingState): StepId | null => {
  // `signedIn` alone is NOT enough, and getting that wrong bounced the wrong people:
  // OnboardingPreviewPage sets it for ANY authenticated visitor the moment the session
  // probe answers, before it has asked the server for progress. An existing customer who
  // merely opens /registration out of curiosity is signed in and has no registration in
  // flight — they must be left on the role picker exactly like a guest, not thrown into
  // a Chabad-centre address form.
  //
  // `path` is the discriminator: it comes from the role step or from the server's
  // `affiliation`, so it is set only for somebody actually in this flow. It is also what
  // decides where to send them, so without it there is no answer anyway.
  if (! s.signedIn || ! s.path) return null;

  return s.path === "anash" ? "a_address" : "s_address";
};

const REQUIREMENTS: Partial<Record<StepId, (s: OnboardingState) => StepId | null>> = {
  role:           refusedOnceSignedIn,
  email:          (s) => refusedOnceSignedIn(s) ?? (s.path ? null : "role"),
  email_code:     (s) => refusedOnceSignedIn(s) ?? (!s.path ? "role" : s.email ? null : "email"),
  account_exists: (s) => refusedOnceSignedIn(s) ?? (s.email ? null : "email"),
  account:        (s) =>
    refusedOnceSignedIn(s)
      ?? (!s.path ? "role"
      : !s.emailVerified ? "email_code"
      : s.path === "anash" && s.intents.length === 0 ? "a_intent"
      : null),
  a_intent:       (s) => refusedOnceSignedIn(s) ?? (s.path !== "anash" ? "role" : s.emailVerified ? null : "email_code"),

  s_address:       (s) => (s.path !== "shliach" ? "role" : requiresSession(s)),
  s_confirm_house: (s) => (s.path !== "shliach" ? "role" : requiresSession(s) ?? (s.houseAddress ? null : "s_address")),
  s_house_details: (s) => (s.path !== "shliach" ? "role" : requiresSession(s) ?? (s.houseAddress ? null : "s_address")),
  s_launched:      requiresSession,
  s_next:          (s) => (s.path !== "shliach" ? "role" : requiresSession(s)),
  s_build_list:    (s) => (s.path !== "shliach" ? "role" : requiresSession(s)),
  s_invite_team:   (s) => (s.path !== "shliach" ? "role" : requiresSession(s)),
  s_list_or_route: (s) => (s.path !== "shliach" ? "role" : requiresSession(s)),

  a_address:      requiresSession,
  a_house:        requiresSession,
  a_availability: requiresSession,
  a_import:       requiresSession,
  a_donate:       requiresSession,
  a_saved:        requiresSession,
};

/**
 * Steps reachable only once signed in — everything gated on `requiresSession`
 * above, i.e. every step except the pre-account ones (role/email/email_code/
 * account/account_exists/a_intent — a_intent is asked BEFORE account, see
 * steps.ts). Used to decide when the server's progress record is the right
 * place to record position/work: a guest has no server record at all, and
 * pre-account work belongs to the browser draft (draftStorage.ts), never
 * the server.
 */
export const PRE_ACCOUNT_STEPS: ReadonlySet<StepId> = new Set<StepId>([
  "role", "email", "email_code", "account", "account_exists", "a_intent",
]);

export const isPostAccountStep = (step: StepId): boolean => !PRE_ACCOUNT_STEPS.has(step);

export const firstUnsatisfiedStep = (state: OnboardingState, target: StepId): StepId => {
  const check = REQUIREMENTS[target];
  if (!check) return target;

  const redirect = check(state);
  if (!redirect) return target;

  // Walk back until a step is satisfied, so a deep link three steps too far
  // lands on the earliest thing actually missing rather than one step back.
  return redirect === target ? target : firstUnsatisfiedStep(state, redirect);
};
